import PropTypes from 'prop-types';
import React from 'react';
import { compact, forEach, get, has, isEmpty, isPlainObject, map, omit, set } from 'lodash';
import Button from '../editors/mui/Button';
import {
  Accordion,
  accordionClasses,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  styled,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import UIUtils from '../../../lib/UIUtils';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import classnames from 'classnames';
import { CredentialField } from 'js/components/elements/pop-up-form/CredentialField';
import { Stack } from 'new/elements/Stack';
import { LayoutUtil } from 'new/utils/LayoutUtil';
import DropdownWithSections from 'js/components/data_sources/DropdownWithSections';

import './PopUpForm.scss';

const muiStyles = {
  expansionPanel: {
    border: 'none',
    backgroundColor: '#141f2b',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    [`&.${accordionClasses.expanded}`]: {
      margin: 'auto',
    },
  },
  expansionPanelSummary: {
    backgroundColor: '#141f2b',
    padding: '16px 25px',
    [`&.${accordionSummaryClasses.expanded}`]: {
      padding: '16px 25px',
    },
    [`.${accordionSummaryClasses.content}`]: {
      margin: 0,
      color: '#fff',
      textDecoration: 'underline',
      fontSize: 12,
      fontWeight: 400,
      '&$expanded': {
        margin: 0,
      },
    },
  },
  expansionPanelDetails: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      color: '#fff',
      padding: '0 25px 10px 25px',
    },
  },
};

const StyledAccordion = styled(Accordion)(muiStyles.expansionPanel);
const StyledAccordionSummary = styled(AccordionSummary)(muiStyles.expansionPanelSummary);
const StyledAccordionDetails = styled(AccordionDetails)(muiStyles.expansionPanelDetails.root);

function shouldLookForOptionalFields(fields = [], isCredentialsForm = false) {
  if (!isCredentialsForm) {
    // for the Add Data Source, Configure FormPage's Advanced Settings
    return false;
  }

  return !!fields.find((item) => !item.required && item.id !== 'cred_desc'); // has at least on optional field
}

// Check if field depends on any parent field that has select-with-dependencies
const isChildOfSelectWithDependencies = (field, allFields, visited = new Set()) => {
  const { dependsOn } = field;

  if (!dependsOn) return false;

  // Prevent infinite recursion
  if (visited.has(field.name)) return false;
  visited.add(field.name);

  // Get parent field name(s) from dependsOn
  let parentNames = [];
  if (dependsOn.allOf || dependsOn.anyOf) {
    const conditions = dependsOn.allOf || dependsOn.anyOf;
    parentNames = conditions.map((c) => c.name).filter(Boolean);
  } else if (dependsOn.name) {
    parentNames = [dependsOn.name];
  }

  // Check if any parent has inputType: 'select-with-dependencies'
  const hasDirectParent = parentNames.some((parentName) => {
    const parentField = allFields.find((f) => f.name === parentName);
    return parentField?.inputType === 'select-with-dependencies';
  });

  if (hasDirectParent) return true;

  // Check for nested dependencies (fields that depend on fields that depend on select-with-dependencies)
  return parentNames.some((parentName) => {
    const parentField = allFields.find((f) => f.name === parentName);
    if (!parentField) return false;

    // Recursively check if the parent is a child of select-with-dependencies
    return isChildOfSelectWithDependencies(parentField, allFields, visited);
  });
};

class PopUpForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasOptionalField: shouldLookForOptionalFields(props.fields, props.isCredentialsForm),
    };
    this._renderFields = this._renderFields.bind(this);
    this._renderField = this._renderField.bind(this);
    this._handleFieldUpdated = this._handleFieldUpdated.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.hasOptionalField && props.isCredentialsForm) {
      const hasOptionalField = shouldLookForOptionalFields(props.fields, props.isCredentialsForm);
      if (hasOptionalField !== state.hasOptionalField) {
        return {
          hasOptionalField: hasOptionalField,
        };
      }
    }

    return null;
  }

  render() {
    const {
      headingIcon,
      heading,
      fields: preFields,
      fieldsMeta,
      submitButtonLabel,
      customButtons,
      buttonsDisabled,
      rootStyle,
      onSubmit,
      onCloseClicked,
      fieldClassNames,
      showCloseButton,
      isModal,
      formPrepend,
    } = this.props;
    let optionalContent = [];
    let formContent;

    let fieldsMap = {};
    forEach(preFields, (field, ix) => {
      fieldsMap[field.name] = ix;
    });
    const fields = LayoutUtil.fillRenderAside(preFields);
    if (fieldsMeta) {
      if (fieldsMeta.advanced && fieldsMeta.advanced.length) {
        let optionalFields = map(fieldsMeta.advanced, (k) => {
          return fields[fieldsMap[k]];
        });
        optionalContent = this._renderFields(optionalFields);
      }
      let mainFields = map(fieldsMeta.basic, (k) => {
        return fields[fieldsMap[k]];
      });
      formContent = this._renderFields(mainFields);
    } else {
      if (this.state.hasOptionalField) {
        let optionalFields = fields.filter((item) => !item.required && item.name !== 'cred_desc');
        let mainFields = fields.filter((item) => item.required || item.name === 'cred_desc');
        optionalContent = this._renderFields(optionalFields);
        formContent = this._renderFields(mainFields);
      } else {
        formContent = this._renderFields(fields);
      }
    }

    const headerClassName = classnames(map(fieldClassNames, (c) => `${c}-header`));

    const optionalFieldsCard = (
      <StyledAccordion squared elevation={1}>
        <StyledAccordionSummary expandIcon={<ExpandMoreIcon fontSize="large" htmlColor="#fff" />}>
          Advanced Settings
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <div className="optional-field-card">{optionalContent}</div>
        </StyledAccordionDetails>
      </StyledAccordion>
    );

    const customWidth = has(rootStyle, 'width') ? rootStyle.width : null;
    const closeButtonLeft = customWidth ? customWidth - 52 : null;
    const closeButtonStyle = closeButtonLeft ? { left: closeButtonLeft } : {};
    const closeButtonClassName = showCloseButton ? 'close-button' : 'close-button-container';

    const rootClassName = classnames(
      'pop-up-form',
      {
        'in-modal': !!isModal,
      },
      this.props.rootClassName,
    );
    return (
      <section className={rootClassName} style={rootStyle}>
        <form autoComplete="new-password">
          {showCloseButton ? (
            <span
              className={closeButtonClassName}
              style={closeButtonStyle}
              onClick={onCloseClicked}
            />
          ) : (
            <span className={closeButtonClassName} style={closeButtonStyle} />
          )}

          {heading ? (
            <header style={{ position: 'relative' }} className="header-container">
              {headingIcon && (
                <div style={{ position: 'absolute', marginRight: '35px' }}>{headingIcon}</div>
              )}
              <h1 className={headerClassName}>{heading}</h1>
            </header>
          ) : null}

          <div className="fields-container">
            {formPrepend}
            {formContent}
            {optionalContent.length >= 1 && (
              <div style={{ margin: '0 -16px', fill: '#fff' }} className="optional-ba">
                {optionalFieldsCard}
              </div>
            )}
          </div>

          {(onSubmit || customButtons) && (
            <div className="save-button-container">
              {customButtons &&
                map(customButtons, (buttonProps, index) => {
                  const { style, labelStyle, label, ...otherButtonProps } = buttonProps;

                  const _key = index + label;
                  const buttonStyle = { height: 36, ...style };
                  const buttonLabelStyle = { fontSize: 10, ...labelStyle };
                  return (
                    <Button
                      key={_key}
                      disabled={buttonsDisabled}
                      rootStyle={buttonStyle}
                      {...otherButtonProps}
                    >
                      <span style={buttonLabelStyle}>{label}</span>
                    </Button>
                  );
                })}
              {onSubmit && (
                <Button
                  buttonTheme="primary"
                  rootStyle={{ marginRight: 0, fontSize: 10, height: 36 }}
                  disabled={buttonsDisabled}
                  onClick={onSubmit}
                >
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>
                    {submitButtonLabel || 'Save'}
                  </span>
                </Button>
              )}
            </div>
          )}
        </form>
      </section>
    );
  }

  _renderFields(fields) {
    const { renderSectionFields, removeSectionHeading } = this.props;
    const [sections, fieldsBySection] = UIUtils.getSectionsFromFields(fields);

    const formContent = [];

    forEach(sections, (section) => {
      const rawFieldsInSection = fieldsBySection[section] || [];
      const mapFields = map(rawFieldsInSection, this._renderField);
      const sectionFields = compact(mapFields);
      const hasRuntimeSelectionField = rawFieldsInSection.some(
        (f) => f.type === 'runtime_selection',
      );
      const isFocused =
        section && this.props?.selectedNavScrollerSection === UIUtils.getSectionElementId(section);

      if (
        section &&
        (!isEmpty(sectionFields) || (hasRuntimeSelectionField && renderSectionFields))
      ) {
        const sectionContent = renderSectionFields
          ? renderSectionFields(section, sectionFields, rawFieldsInSection)
          : null;

        // If renderSectionFields explicitly returns null, suppress the entire section (heading included).
        if (renderSectionFields && sectionContent === null) return;

        const id = UIUtils.getSectionElementId(section);

        formContent.push(
          <div
            key={id}
            aria-label="section-container"
            className={classnames({ isPopUpFormFocused: isFocused })}
          >
            {!removeSectionHeading && (
              <h2 id={id} className="section-heading" key={'section-heading-' + section}>
                {section}
              </h2>
            )}
            <div className="form-content" key={'section-fields-' + section}>
              {sectionContent ?? [...sectionFields]}
            </div>
          </div>,
        );
      } else
        formContent.push(
          <div className="form-content" key={'section-fields-' + section}>
            {[...sectionFields]}
          </div>,
        );
    });

    return formContent;
  }

  _renderField(field) {
    const { fieldClassNames, data = {}, fields } = this.props;
    if (field.type === 'runtime_selection') return null;

    if (UIUtils.shouldHideField(field, data) === true || !UIUtils.shouldRenderField(field, data)) {
      return null;
    }

    // Check if field should be disabled based on disabledOn configuration
    let isFieldDisabled = false;
    if (field.disabledOn && Array.isArray(field.disabledOn)) {
      isFieldDisabled = field.disabledOn.every((condition) => {
        const fieldValue = data[condition.name];
        return fieldValue === condition.value;
      });
    }

    // for button fields, check if it depends on anything
    // Prevent double rendering: skip if this field is a child of select-with-dependencies
    if (isChildOfSelectWithDependencies(field, fields)) {
      return null;
    }

    let fieldInputContent;

    const defaultFieldValue =
      field.type === 'nonce'
        ? UIUtils.generateNonce()
        : has(field, 'default')
          ? field.default
          : null;
    const name = field.name || field.fieldName;

    const valueFromConfigPath = field.configPath ? data?.[field.configPath]?.[name] : null;

    const valueFromData = data?.[name];

    const currentFieldValue = valueFromConfigPath ?? valueFromData ?? defaultFieldValue;

    const isRequired = field.required && !isPlainObject(field.required) && !has(field, 'default');
    const showRequiredIndicator = !(
      field.showRequiredIndicator === false || this.props.showRequiredIndicator === false
    );

    const key = 'grp-' + field.name;
    const id = field.id || 'form-text' + field.name;
    const fieldLabelClassName = classnames(
      map(fieldClassNames, (c) => `${c}-${field.name} ${'field-label-name'}`),
    );

    if (field.type === 'script_credential') {
      return (
        <Stack key={key} padding="0 16px 16px 16px">
          <CredentialField field={field} onChange={this._handleFieldUpdated} values={data} />
        </Stack>
      );
    }

    if (field.type === 'ai_function') {
      // Skip rendering ai_function fields that are children of select-with-dependencies
      // They will be rendered within the SelectWithDependencies component instead
      if (isChildOfSelectWithDependencies(field, this.props.fields)) {
        return null;
      }

      return (
        <div className="field-builder-container" key={`grp-${field.name}`}>
          <label className={fieldLabelClassName}>{field.display_name}</label>
          <DropdownWithSections
            field={field}
            sourceConfig={data}
            onSourceConfigChanged={(newSourceConfig) => {
              this.props.onSourceConfigChanged(newSourceConfig);
            }}
            onFieldUpdated={(field, value) => {
              this._handleFieldUpdated(field, value);
            }}
          />
        </div>
      );
    }

    // Skip renderAside for select-with-dependencies fields and their dependent fields to avoid duplicate rendering
    if (
      field.renderAside &&
      field.inputType !== 'select-with-dependencies' &&
      !isChildOfSelectWithDependencies(field, this.props.fields)
    ) {
      fieldInputContent = (
        <Stack flex={1} width="100%" padding="16px" direction="row" gap="16px" key={key}>
          <Stack flex={1} maxWidth="40%">
            <FieldBuilderRedesigned
              field={field}
              currentFieldValue={currentFieldValue}
              isRequired={isRequired}
              showRequiredIndicator={showRequiredIndicator}
              id={id}
              onChange={this._handleFieldUpdated}
              fieldLabelClassName={fieldLabelClassName}
              disabled={isFieldDisabled}
              allFields={this.props.fields}
              currentValues={data}
              onButtonFieldClicked={this.props.onButtonFieldClicked}
            />
          </Stack>
          <Stack data-testid="fields-aside" className="fields-aside" flex={1} maxWidth="60%">
            {this._renderFields(field.renderAside)}
          </Stack>
        </Stack>
      );
    } else {
      fieldInputContent = (
        <div className="field-builder-container" key={key}>
          <FieldBuilderRedesigned
            field={field}
            currentFieldValue={currentFieldValue}
            isRequired={isRequired}
            showRequiredIndicator={showRequiredIndicator}
            id={id}
            onChange={this._handleFieldUpdated}
            fieldLabelClassName={fieldLabelClassName}
            disabled={isFieldDisabled}
            allFields={this.props.fields}
            currentValues={data}
            onButtonFieldClicked={this.props.onButtonFieldClicked}
          />
        </div>
      );
    }

    return fieldInputContent;
  }

  _handleFieldUpdated = (name, value, isButtonField = false) => {
    const { fields, data, onFieldUpdated, onButtonFieldClicked } = this.props;
    let newData = { ...(data || {}) };

    if (name) {
      const field = fields.find((f) => f.name === name);
      if (field?.configPath) {
        set(newData, `${field.configPath}[${name}]`, value);
      } else {
        newData[name] = value;
      }
    }

    //check if any conditional params are to be rendered. if they are missing in source config add their default value.
    newData = UIUtils.configWithMissingDefaults(newData, fields);

    // omit fields which shouldn't be rendered
    let invisibleFieldNames = fields
      .filter((field) => !UIUtils.shouldRenderField(field, newData))
      .map((field) => field.name);
    let visibleFieldData = omit(newData, invisibleFieldNames);

    // temp hack: omit 2nd-level dependsOn fields, i.e. field A depends on field
    // B and field C depends on field B. a better recursive function should be
    // written to handle that case.
    invisibleFieldNames = fields
      .filter((field) => !UIUtils.shouldRenderField(field, visibleFieldData))
      .map((field) => field.name);
    visibleFieldData = omit(visibleFieldData, invisibleFieldNames);

    if (isButtonField && onButtonFieldClicked) {
      onButtonFieldClicked(name, value, visibleFieldData);
    } else {
      onFieldUpdated(name, value, visibleFieldData);
    }
  };
}

PopUpForm.propTypes = {
  heading: PropTypes.string,
  headingIcon: PropTypes.object,
  /*
  Shape of an object in the fields array:
  {
    id,
    name,
    type,
    display_name (label),
    value
    required,
    showRequiredIndicator,
    style
  }
  */
  fields: PropTypes.array.isRequired,
  data: PropTypes.object,
  showRequiredIndicator: PropTypes.bool,
  customButtons: PropTypes.array, // array of valid props for Button
  buttonsDisabled: PropTypes.bool,
  rootStyle: PropTypes.object,
  onCloseClicked: PropTypes.func,
  onFieldUpdated: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onButtonFieldClicked: PropTypes.func,
  formPrepend: PropTypes.node,
};

PopUpForm.defaultProps = {
  showRequiredIndicator: true,
  buttonsDisabled: false,
  showCloseButton: true,
  rootStyle: {},
  fieldClassNames: [],
  onButtonFieldClicked: null,
  isModal: false,
  formPrepend: null,
};

export default PopUpForm;
